import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  ManageRoleItem,
  ManageRoleMenuItem,
  ManageRoleRightDraftItem,
  ManageRolePagination,
} from './manage-role.interfaces';
import { createManageRoleFormValue } from './manage-role.data';

type ManageRoleModalMode = 'create' | 'edit' | 'view';
type ManageRoleSortField = 'role_name';
type ManageRoleSortOrder = 'asc' | 'desc';
type ManageRoleQueryValue = string | number | null | undefined;
type ManageRoleQueryParams = Record<string, ManageRoleQueryValue>;

@Component({
  selector: 'app-manage-role-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-role.html',
  styleUrl: './manage-role.scss',
})
export class ManageRolePage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filterForm: UntypedFormGroup;
  protected readonly roleForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected roleModalOpen = false;
  protected deleteModalOpen = false;
  protected rightsModalOpen = false;
  protected modalMode: ManageRoleModalMode = 'create';
  protected selectedRole: ManageRoleItem | null = null;
  protected rightsDraft: ManageRoleRightDraftItem[] = [];
  protected updateExistingUsers = false;
  protected formSubmitted = false;
  protected sortBy: ManageRoleSortField | null = null;
  protected sortOrder: ManageRoleSortOrder = 'asc';

  protected roles: ManageRoleItem[] = [];
  protected filteredRoles: ManageRoleItem[] = [];
  protected menus: ManageRoleMenuItem[] = [];
  protected pagination: ManageRolePagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.roleForm = this.fb.group({
      role_name: ['', [Validators.required, Validators.maxLength(50)]],
      description: [''],
    });
  }

  ngOnInit(): void {
    this.loadRoles(1);

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRoles(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.loadRoles(1);
  }

  protected openAddRoleModal(): void {
    this.modalMode = 'create';
    this.selectedRole = null;
    this.roleForm.enable({ emitEvent: false });
    this.roleForm.reset(createManageRoleFormValue());
    this.formSubmitted = false;
    this.roleModalOpen = true;
  }

  protected openEditRoleModal(role: ManageRoleItem): void {
    this.modalMode = 'edit';
    this.selectedRole = role;
    this.roleForm.enable({ emitEvent: false });
    this.roleForm.reset({
      role_name: role.role_name,
      description: role.description ?? '',
    });
    this.formSubmitted = false;
    this.roleModalOpen = true;
  }

  protected openViewRoleModal(role: ManageRoleItem): void {
    this.modalMode = 'view';
    this.selectedRole = role;
    this.roleForm.enable({ emitEvent: false });
    this.roleForm.reset({
      role_name: role.role_name,
      description: role.description ?? '',
    });
    this.formSubmitted = false;
    this.roleModalOpen = true;
    this.roleForm.disable({ emitEvent: false });
  }

  protected openDeleteRoleModal(role: ManageRoleItem): void {
    this.selectedRole = role;
    this.deleteModalOpen = true;
  }

  protected openRightsModal(role: ManageRoleItem): void {
    this.selectedRole = role;
    this.rightsDraft = this.buildRightsDraft(role);
    this.updateExistingUsers = false;
    this.rightsModalOpen = true;
  }

  protected closeRoleModal(): void {
    this.roleModalOpen = false;
    this.selectedRole = null;
    this.modalMode = 'create';
    this.roleForm.enable({ emitEvent: false });
    this.roleForm.reset(createManageRoleFormValue());
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedRole = null;
  }

  protected closeRightsModal(): void {
    this.rightsModalOpen = false;
    this.selectedRole = null;
    this.rightsDraft = [];
    this.updateExistingUsers = false;
  }

  protected saveRole(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.roleForm.invalid) {
      this.formSubmitted = true;
      this.roleForm.markAllAsTouched();
      return;
    }

    const payload = this.buildRolePayload();
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_ROLE : AuthEndPoints.EDIT_ROLE;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeRoleModal();
          this.loadRoles();
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedRole) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_ROLE, ApiMethod.DELETE, { id: this.selectedRole.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadRoles();
        },
      });
  }

  protected saveRights(): void {
    if (!this.selectedRole) {
      return;
    }

    const payload = {
      role_id: this.selectedRole.id,
      menu_rights: this.rightsDraft.map((row) => ({
        menu_id: row.menu_id,
        is_read: row.is_read,
        is_write: row.is_write,
      })),
      update_existing_users: this.updateExistingUsers,
    };

    this.httpSvc
      .requestCall(AuthEndPoints.MANAGE_MENU_ROLEWISE, ApiMethod.PATCH, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeRightsModal();
          this.loadRoles();
        },
      });
  }

  protected onReadChange(row: ManageRoleRightDraftItem, checked: boolean): void {
    row.is_read = checked;
    if (!checked) {
      row.is_write = false;
    }
  }

  protected onWriteChange(row: ManageRoleRightDraftItem, checked: boolean): void {
    row.is_write = checked;
    row.is_read = checked;
  }

  protected toggleAllRead(checked: boolean): void {
    this.rightsDraft = this.rightsDraft.map((row) => ({
      ...row,
      is_read: checked,
      is_write: checked ? row.is_write : false,
    }));
  }

  protected toggleAllWrite(checked: boolean): void {
    this.rightsDraft = this.rightsDraft.map((row) => ({
      ...row,
      is_read: checked,
      is_write: checked,
    }));
  }

  protected areAllReadSelected(): boolean {
    return this.rightsDraft.length > 0 && this.rightsDraft.every((row) => row.is_read);
  }

  protected areAllWriteSelected(): boolean {
    return this.rightsDraft.length > 0 && this.rightsDraft.every((row) => row.is_write);
  }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.roleForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected trackByRoleId(_: number, role: ManageRoleItem): number {
    return role.id;
  }

  protected toggleSort(field: ManageRoleSortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadRoles(1);
  }

  protected isSortAscending(field: ManageRoleSortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageRoleSortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected trackByRightId(_: number, right: ManageRoleRightDraftItem): number {
    return right.menu_id;
  }

  protected isChildRight(right: ManageRoleRightDraftItem): boolean {
    return right.depth > 0;
  }

  protected getRoleSummary(role: ManageRoleItem): string {
    const activeRights = role.menu_rights.filter((right) => right.is_read || right.is_write);
    if (!activeRights.length) {
      return 'No rights assigned';
    }
    return `${activeRights.length} menu${activeRights.length > 1 ? 's' : ''}`;
  }

  private loadRoles(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_ROLES_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.finishLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          const resolved = this.resolveRolesPayload(response);
          this.roles = resolved.roles;
          this.filteredRoles = [...resolved.roles];
          this.menus = resolved.menus;
          this.pagination = resolved.pagination ?? this.pagination;
          this.finishLoading();
        },
        error: () => {
          this.roles = [];
          this.filteredRoles = [];
          this.menus = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
          this.finishLoading();
        },
      });
  }

  private buildRolePayload(): Record<string, unknown> {
    const formValue = this.roleForm.getRawValue();

    if (this.modalMode === 'create') {
      return {
        role_name: String(formValue.role_name ?? '').trim(),
        role_description: String(formValue.description ?? '').trim(),
      };
    }

    if (!this.selectedRole) {
      return {};
    }

    const payload: Record<string, unknown> = {
      id: this.selectedRole.id,
    };

    const roleName = String(formValue.role_name ?? '').trim();
    const description = String(formValue.description ?? '').trim();

    if (roleName !== this.selectedRole.role_name) {
      payload['role_name'] = roleName;
    }

    if (description !== (this.selectedRole.description ?? '')) {
      payload['role_description'] = description;
    }

    return payload;
  }

  private resolveRolesPayload(response: unknown): {
    roles: ManageRoleItem[];
    menus: ManageRoleMenuItem[];
    pagination: ManageRolePagination | null;
  } {
    const value = this.extractValue(response);
    if (!value || typeof value !== 'object') {
      return { roles: [], menus: [], pagination: null };
    }

    const roles = Array.isArray((value as { roles?: unknown }).roles)
      ? (value as { roles: unknown[] }).roles.map((item) => this.mapRoleItem(item))
      : [];
    const menus = Array.isArray((value as { menus?: unknown }).menus)
      ? (value as { menus: unknown[] }).menus.map((item) => this.mapMenuItem(item))
      : [];

    const pagination = this.extractPagination(value);

    return { roles, menus, pagination };
  }

  private extractValue(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const rootValue = (response as { value?: unknown }).value;
    if (rootValue && typeof rootValue === 'object') {
      return rootValue;
    }

    return null;
  }

  private mapRoleItem(item: unknown): ManageRoleItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      id: Number(record['id']),
      serial_no: Number(record['serial_no'] ?? 0),
      role_name: typeof record['role_name'] === 'string' ? record['role_name'] : '',
      description: typeof record['role_description'] === 'string' ? record['role_description'] : typeof record['description'] === 'string' ? record['description'] : null,
      user_count: record['user_count'] === null || record['user_count'] === undefined ? 0 : Number(record['user_count']),
      menu_rights: Array.isArray(record['menu_rights'])
        ? record['menu_rights'].map((right) => this.mapRightItem(right))
        : [],
    };
  }

  private mapMenuItem(item: unknown): ManageRoleMenuItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      id: Number(record['id']),
      serial_no: Number(record['serial_no'] ?? 0),
      parent_menu_id: record['parent_menu_id'] === null || record['parent_menu_id'] === undefined ? null : Number(record['parent_menu_id']),
      menu_name: typeof record['menu_name'] === 'string' ? record['menu_name'] : null,
      menu_url: typeof record['menu_url'] === 'string' ? record['menu_url'] : null,
      menu_code: typeof record['menu_code'] === 'string' ? record['menu_code'] : null,
      order_by: record['order_by'] === null || record['order_by'] === undefined ? null : Number(record['order_by']),
      icon_class: typeof record['icon_class'] === 'string' ? record['icon_class'] : null,
    };
  }

  private mapRightItem(item: unknown): ManageRoleRightDraftItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      menu_id: Number(record['menu_id']),
      serial_no: Number(record['serial_no'] ?? 0),
      menu_name: typeof record['menu_name'] === 'string' ? record['menu_name'] : 'Untitled menu',
      parent_menu_id: record['parent_menu_id'] === null || record['parent_menu_id'] === undefined ? null : Number(record['parent_menu_id']),
      parent_menu_name: typeof record['parent_menu_name'] === 'string' ? record['parent_menu_name'] : null,
      depth: record['depth'] === null || record['depth'] === undefined ? 0 : Number(record['depth']),
      is_read: Boolean(record['is_read']),
      is_write: Boolean(record['is_write']),
    };
  }

  private extractPagination(value: unknown): ManageRolePagination | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const pagination = (value as { pagination?: unknown }).pagination;
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }

    const record = pagination as Record<string, unknown>;
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? 10),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private buildRightsDraft(role: ManageRoleItem): ManageRoleRightDraftItem[] {
    const menuMap = new Map(this.menus.map((menu) => [menu.id, menu]));
    const rightsByMenuId = new Map(role.menu_rights.map((right) => [right.menu_id, right]));

    return this.sortMenusByHierarchy(this.menus).map((menu) => {
      const existingRight = rightsByMenuId.get(menu.id);
      const depth = this.getMenuDepth(menu.id, menuMap);
      return {
        menu_id: menu.id,
        serial_no: menu.serial_no,
        menu_name: menu.menu_name ?? 'Untitled menu',
        parent_menu_id: menu.parent_menu_id,
        parent_menu_name: menu.parent_menu_id === null ? null : menuMap.get(menu.parent_menu_id)?.menu_name ?? null,
        depth,
        is_read: existingRight?.is_read ?? false,
        is_write: existingRight?.is_write ?? false,
      };
    });
  }

  private sortMenusByHierarchy(menus: ManageRoleMenuItem[]): ManageRoleMenuItem[] {
    const groupedMenus = new Map<number | null, ManageRoleMenuItem[]>();

    menus.forEach((menu) => {
      const parentId = menu.parent_menu_id ?? null;
      const group = groupedMenus.get(parentId) ?? [];
      group.push(menu);
      groupedMenus.set(parentId, group);
    });

    const sortGroup = (items: ManageRoleMenuItem[]): ManageRoleMenuItem[] =>
      [...items].sort((left, right) => {
        const leftOrder = left.order_by ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.order_by ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return (left.menu_name ?? '').localeCompare(right.menu_name ?? '');
      });

    const walk = (parentId: number | null): ManageRoleMenuItem[] => {
      const currentLevel = sortGroup(groupedMenus.get(parentId) ?? []);
      return currentLevel.flatMap((menu) => [menu, ...walk(menu.id)]);
    };

    return walk(null);
  }

  private getMenuDepth(menuId: number, menuMap: Map<number, ManageRoleMenuItem>): number {
    let depth = 0;
    let currentMenu = menuMap.get(menuId);

    while (currentMenu?.parent_menu_id !== null && currentMenu?.parent_menu_id !== undefined) {
      depth += 1;
      currentMenu = menuMap.get(currentMenu.parent_menu_id);
    }

    return depth;
  }

  private buildQueryString(params: ManageRoleQueryParams): string {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === 'all') {
        return;
      }

      queryParams.set(key, String(value));
    });

    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return 'No records';
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `Showing ${start} to ${end} of ${this.pagination.total_records} results`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (!totalPages) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages: number[] = [];
    const addPage = (pageNo: number): void => {
      if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
        pages.push(pageNo);
      }
    };

    addPage(1);

    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);

    if (windowStart > 2) {
      pages.push(-1);
    }

    for (let page = windowStart; page <= windowEnd; page += 1) {
      addPage(page);
    }

    if (windowEnd < totalPages - 1) {
      pages.push(-1);
    }

    addPage(totalPages);
    return pages;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadRoles(pageNo);
  }

  private finishLoading(): void {
    if (!this.isLoading) {
      return;
    }

    this.isLoading = false;
    this.cdr.detectChanges();
  }
}
